// src/features/files/components/MoveFilesDialog.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  DriveFileMove as MoveIcon,
} from '@mui/icons-material';

import { filesAPI } from '../services/files';
import { foldersAPI } from '../services/folders';
import { getFolderLink, getFolderName } from '../utils/getFolderName';
import { FolderGlyph } from './FileExplorer/modules/FolderGlyph';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

/**
 * Picks a folder and moves files (and/or `folders`) there, the keyboard- and
 * touch-friendly alternative to dragging. `startTrail` is the breadcrumb trail of
 * the folder they are in ([{ id, name, raw }], root has id null), so browsing starts there.
 */
export default function MoveFilesDialog({ open, files = [], folders: movingFolders = [], startTrail, onClose, onMoved }) {
  const { t } = useTranslation(['files', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const rootCrumb = { id: null, name: t('files:root') };
  const [trail, setTrail] = useState([rootCrumb]);
  const [folders, setFolders] = useState(null);
  const [moving, setMoving] = useState(false);

  const sourceFolderId = startTrail?.length ? startTrail[startTrail.length - 1].id : null;
  const target = trail[trail.length - 1];
  const itemCount = files.length + movingFolders.length;
  // A folder can't go into itself, so it isn't offered as a place to browse into
  const movingFolderIds = movingFolders.map(folder => folder.folder_id);
  const browsable = folders && folders.filter(folder => !movingFolderIds.includes(folder.folder_id));

  // Start where the files are each time the dialog opens
  useEffect(() => {
    if (open) setTrail(startTrail?.length ? startTrail : [{ id: null, name: t('files:root') }]);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setFolders(null);
    const request = target.id ? foldersAPI.getFolderContents(target.id) : foldersAPI.getRootContents();
    request
      .then((res) => {
        const data = res.data?.data || {};
        if (!cancelled) setFolders([...(data.folders || []), ...(data.subfolders || [])]);
      })
      .catch(() => { if (!cancelled) setFolders([]); });
    return () => { cancelled = true; };
  }, [open, target.id]);

  const handleMove = async () => {
    setMoving(true);
    let moved = 0;
    for (const file of files) {
      try {
        await filesAPI.moveFile(file.file_id, target.id);
        moved += 1;
      } catch {
        // Counted below
      }
    }
    let error = null;
    for (const folder of movingFolders) {
      try {
        await foldersAPI.moveFolder(folder.folder_id, target.id);
        moved += 1;
      } catch (err) {
        error = err.response?.data?.message;
      }
    }
    setMoving(false);
    if (moved) {
      showSuccess(movingFolders.length && !files.length
        ? t('files:move.folder_done', { name: getFolderName(movingFolders[0].folder_name), folder: target.name })
        : t('files:move.done', { count: moved, folder: target.name }));
    }
    if (moved < itemCount) showError(error || t('files:errors.move_failed'));
    onMoved?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={moving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {itemCount === 1
          ? t('files:move.title_one', { name: files[0]?.filename ?? getFolderName(movingFolders[0]?.folder_name) })
          : t('files:move.title_other', { count: itemCount })}
      </DialogTitle>
      <DialogContent>
        <Breadcrumbs sx={{ mb: 1.5 }} separator={<ChevronRightIcon fontSize="small" />}>
          {trail.map((crumb, index) => (index === trail.length - 1 ? (
            <Typography key={crumb.id ?? 'root'} color="text.primary" fontWeight={500}>{crumb.name}</Typography>
          ) : (
            <Link
              key={crumb.id ?? 'root'}
              component="button"
              type="button"
              underline="hover"
              onClick={() => setTrail(trail.slice(0, index + 1))}
            >
              {crumb.name}
            </Link>
          )))}
        </Breadcrumbs>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 240, maxHeight: 360, overflowY: 'auto' }}>
          {browsable === null ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
          ) : browsable.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              {t('files:move.no_subfolders')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {browsable.map(folder => (
                <ListItemButton
                  key={folder.folder_id}
                  onClick={() => setTrail([...trail, { id: folder.folder_id, name: getFolderName(folder.folder_name), raw: folder.folder_name }])}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}><FolderGlyph link={getFolderLink(folder, target.raw)} /></ListItemIcon>
                  <ListItemText primary={getFolderName(folder.folder_name)} />
                  <ChevronRightIcon fontSize="small" color="action" />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={moving}>{t('common:buttons.cancel')}</Button>
        <Button
          variant="contained"
          startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <MoveIcon />}
          onClick={handleMove}
          disabled={moving || target.id === sourceFolderId}
        >
          {t('files:move.here', { folder: target.name })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
